import { CheckIcon } from "@heroicons/react/20/solid";
import {
  useMeeting,
  useParticipant,
  usePubSub,
} from "@videosdk.live/react-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast, Bounce } from "react-toastify";
import useIsMobile from "../utils/useIsMobile";
import useIsTab from "../utils/useIsTab";
import useIsBigDesktop from "../utils/useIsBigScreen";
import { useAppContext } from "../AppContextDef";
import { useTranslation } from "react-i18next";
import useIsLGDesktop from "../utils/useIsLGDesktop";
import { fontMap } from "../utils/common";
import usePubsubFunction from "../utils/usePubsubFunction";

const ParticipantView = ({
  currentPage,
  capturedImage,
  setCapturedImage,
  setAgreed,
  agreed,
  abortSignal,
  videoHeight,
}) => {
  let isMobile = useIsMobile();
  let isTab = useIsTab();
  let isBigDesktop = useIsBigDesktop();
  let isLGDesktop = useIsLGDesktop();
  const pubsubPublishMethod = usePubsubFunction()
  const micRef = useRef(null);
  const videoRef = useRef(null);
  const [isVerified, setisVerified] = useState(false);
  const isRequestInProgress = useRef(false);
  const { publish: faceDetectErrorPublish } = usePubSub(`FACE_DETECT_ERROR`);
  const { publish: faceVerifiedPublish } = usePubSub(`FACE_VERIFIED`);
  const { publish: agreedPublish } = usePubSub(`AGREED`);
  const { publish: abortSignalPublish } = usePubSub(`ABORT_SIGNAL`);
  let isMobileOrTab = isMobile || isTab;

  const { selectedLanguageCode, isMeetingEnded, allDetails } = useAppContext();

  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  useEffect(() => {
    changeLanguage(selectedLanguageCode);
  }, [selectedLanguageCode]);
  const { localParticipant } = useMeeting({});
  const {
    webcamStream,
    micStream,
    webcamOn,
    micOn,
    isLocal,
    captureImage,
  } = useParticipant(localParticipant?.id);

  const fontFamilyCSS = fontMap[selectedLanguageCode] || "Lato";

  const spoofCheckDoneRef = useRef(false);
  const faceDetectionDoneRef = useRef(false)
  const faceMatchDoneRef = useRef(false)
  const capturedImageRef = useRef(null);

  useEffect(() => {
    capturedImageRef.current = capturedImage;
  }, [capturedImage]);



  async function imageCapture() {
    if (webcamOn) {
      let base64Data = await captureImage(500, 400);

      let sendImage = "data:image/jpeg;base64," + base64Data;
      const response = await verifyNumberOfFaces(sendImage);

      if (response === 1) {
        if (!spoofCheckDoneRef.current) {
          // Only call if not already spoof-verified
          let spoofResponse = true;

          // for create delay
          const delay = (ms) =>
            new Promise((resolve) => setTimeout(resolve, ms));

          while (spoofResponse) {
            // Keep calling until no spoof detected

            // Capture the latest image before every spoof check
            base64Data = await captureImage(500, 400);
            sendImage = "data:image/jpeg;base64," + base64Data;

            spoofResponse = await detectSpoof(sendImage);
            if (spoofResponse) {
              setAgreed(false);
              pubsubPublishMethod(agreedPublish, "Agreed", { persist: true }, { isAgreed: false });
              if (!abortSignal.signal.aborted) {
                handleToastMessage({
                  message: `${t(
                    "Make sure a real person is in view and looking towards camera in a well-lit area!"
                  )}`,
                });
                pubsubPublishMethod(faceDetectErrorPublish,
                  "Spoof",
                  { persist: true },
                  { err: `SPOOF_DETECTED` }
                );
                pubsubPublishMethod(abortSignalPublish, "Abort", { persist: true }, { isAbort: false });
              }
            }
            // call for 3 sec delay
            await delay(3000);
          }

          // Mark spoof check as completed if no spoof is detected
          spoofCheckDoneRef.current = true;
        }

        // const spoofResponse = await detectSpoof(sendImage);
        // if (!spoofResponse) {
        if (currentPage === "PersonalDetails") {
          if (capturedImageRef.current === null) {
            setCapturedImage(sendImage);
          }
          setAgreed(true);
          setisVerified(true);
        } else {
          if (currentPage === "PolicyDetails" && faceMatchDoneRef.current) {
            return;
          }
          const matchResponse = await faceMatch(sendImage, capturedImage);
          if (matchResponse) {
            setAgreed(true);
            setisVerified(true);
            if (currentPage === "PolicyDetails") faceMatchDoneRef.current = true
          } else {
            setAgreed(false);
            pubsubPublishMethod(agreedPublish, "Agreed", { persist: true }, { isAgreed: false });
            if (!abortSignal.signal.aborted) {
              handleToastMessage({
                message: `${t("Face Match Unsuccessful!")}`,
              });
              pubsubPublishMethod(abortSignalPublish, "Abort", { persist: true }, { isAbort: false });
            }
            pubsubPublishMethod(faceDetectErrorPublish,
              "Error",
              { persist: true },
              { err: `FACE_MATCH_UNSUCESSFUL` },
            );
          }
        }
        // } else {
        //   setAgreed(false);
        //   agreedPublish({ isAgreed: false }, { persist: true });
        //   if (!abortSignal.signal.aborted) {
        //     handleToastMessage({
        //       message: `${t(
        //         "Make sure a real person is in view and looking towards camera in a well-lit area!"
        //       )}`,
        //     });
        //     faceDetectErrorPublish(
        //       { err: `SPOOF_DETECTED` },
        //       { persist: true }
        //     );
        //     abortSignalPublish({ isAbort: false }, { persist: true });
        //   }
        // }
      } else if (response > 1) {
        setAgreed(false);
        pubsubPublishMethod(agreedPublish, "Agreed", { persist: true }, { isAgreed: false });
        if (!abortSignal.signal.aborted) {
          handleToastMessage({
            message: `${t(
              "Multiple faces detected. Please make sure only one person is in view!"
            )}`,
          });
          pubsubPublishMethod(abortSignalPublish, "Abort", { persist: true }, { isAbort: false });
        }
        pubsubPublishMethod(faceDetectErrorPublish,
          "MULTIPLE_FACES_DETECTED",
          { persist: true },
          { err: `MULTIPLE_FACES_DETECTED` }
        );
      } else {
        setAgreed(false);
        pubsubPublishMethod(agreedPublish, "Agreed", { persist: true }, { isAgreed: false });
        if (!abortSignal.signal.aborted) {
          handleToastMessage({
            message: `${t(
              "No face detected. Please ensure your face is clearly visible."
            )}`,
          });
          pubsubPublishMethod(abortSignalPublish, "Abort", { persist: true }, { isAbort: false });
        }
        pubsubPublishMethod(faceDetectErrorPublish,
          "NO_FACE_DETECTED",
          { persist: true },
          { err: `NO_FACE_DETECTED` }
        );
      }
    } else {
      setAgreed(false);
      pubsubPublishMethod(agreedPublish, "Agreed", { persist: true }, { isAgreed: false });
      if (!abortSignal.signal.aborted) {
        handleToastMessage({
          message: `${t(
            "You don't have camera permissions, Please give permissions manually"
          )}`,
        });
        pubsubPublishMethod(abortSignalPublish, "Abort", { persist: true }, { isAbort: false });
      }
      pubsubPublishMethod(faceDetectErrorPublish,
        "NO_CAMERA_PERMISSIONS",
        { persist: true },
        { err: `NO_CAMERA_PERMISSIONS` }
      );
    }
  }

  const handleToastMessage = ({ message }) => {
    new Audio(`https://cdn.videosdk.live/icici/error_1.wav`).play();
    toast.error(message, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      style: { fontFamily: fontFamilyCSS },
    });
  };

  const verifyNumberOfFaces = async (sendImage) => {
    const controller = abortSignal && abortSignal;
    const signal = controller.signal;

    if ((currentPage === "PersonalDetails" || currentPage === "PolicyDetails") && faceDetectionDoneRef.current) {
      return 1;
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_VIDEOSDK_URL}/ai/v1/face-verification/detect-faces`,
        {
          method: "POST",
          headers: {
            authorization: `${allDetails?.videoSDKToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            img: sendImage,
          }),
          signal,
        }
      );

      if (res.status === 400) {
        console.error("No faces detected or bad request.");
        return null;
      }

      const { number_of_faces } = await res.json();
      if ((currentPage === "PersonalDetails" || currentPage === "PolicyDetails") && number_of_faces === 1) {
        faceDetectionDoneRef.current = true
      }
      return number_of_faces;
    } catch (error) {
      if (error.name === "AbortError") {
        return null;
      }
      console.error("Error during face detection:", error);
      return null;
    }
  };

  const detectSpoof = async (sendImage) => {
    const controller = abortSignal && abortSignal;
    const signal = controller.signal;

    try {
      const res = await fetch(
        `${process.env.REACT_APP_VIDEOSDK_URL}/ai/v1/face-verification/detect-spoof`,
        {
          method: "POST",
          headers: {
            authorization: `${allDetails?.videoSDKToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            img: sendImage,
          }),
          signal,
        }
      );

      if (res.status === 400) {
        console.error("No faces detected or bad request.");
        return true;
      }

      const { spoof_detected } = await res.json();
      return spoof_detected;
    } catch (error) {
      if (error.name === "AbortError") {
        return true;
      }
      console.error("Error during face detection:", error);
      return true;
    }
  };

  async function faceMatch(img1, img2) {
    const controller = abortSignal && abortSignal;
    const signal = controller.signal;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_VIDEOSDK_URL}/ai/v1/face-verification/verify`,
        {
          method: "POST",
          headers: {
            authorization: `${allDetails?.videoSDKToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            img1: img1,
            img2: img2,
          }),
          signal,
        }
      );

      if (res.status === 400) {
        console.error("Error in face verification: Bad request.");
        return false;
      }

      const { verified } = await res.json();
      return verified;
    } catch (error) {
      if (error.name === "AbortError") {
        return false;
      }
      console.error("Error during face match verification:", error);
      return false;
    }
  }

  const videoStream = useMemo(() => {
    if (webcamOn && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      return mediaStream;
    }
    return null;
  }, [webcamStream, webcamOn]);

  useEffect(() => {
    if (webcamOn && webcamStream && !isMeetingEnded) {
      // Call immediately when the effect runs
      const makeRequest = () => {
        if (!isRequestInProgress.current) {
          isRequestInProgress.current = true;
          imageCapture().finally(() => (isRequestInProgress.current = false));
        }
      };

      if (currentPage !== "PersonalDetails") {
        makeRequest();
      }

      // Set interval for subsequent calls
      const intervalId = setInterval(makeRequest, 5000);

      return () => clearInterval(intervalId);
    }
  }, [webcamOn, webcamStream, isMeetingEnded, isRequestInProgress]);

  useEffect(() => {
    if (micRef.current) {
      if (micOn && micStream) {
        try {
          const mediaStream = new MediaStream();
          mediaStream.addTrack(micStream.track);
          micRef.current.srcObject = mediaStream;
          micRef.current
            .play()
            .catch((error) =>
              console.error("micRef.current.play() failed", error)
            );
        } catch (error) {
          console.error("Error handling mic stream:", error);
          micRef.current.srcObject = null;
        }
      } else {
        micRef.current.srcObject = null;
      }
    }
  }, [micStream, micOn]);

  useEffect(() => {
    if (videoRef.current) {
      if (webcamOn && videoStream) {
        try {
          videoRef.current.srcObject = videoStream;
          videoRef.current.setAttribute('playsinline', '');
          videoRef.current.setAttribute('x5-playsinline', '');
          videoRef.current.setAttribute('webkit-playsinline', '');
          videoRef.current
            .play()
            .catch((error) =>
              console.error("videoRef.current.play() failed", error)
            );
        } catch (error) {
          console.error("Error handling webcam stream:", error);
          videoRef.current.srcObject = null;
        }
      } else {
        videoRef.current.srcObject = null;
      }
    }
  }, [videoStream, webcamOn]);

  useEffect(() => {
    if (isVerified && agreed) {
      pubsubPublishMethod(agreedPublish, "Agreed", { persist: true }, { isAgreed: true });
      pubsubPublishMethod(faceVerifiedPublish, "Verified", { persist: true }, { isVerified: true });
    }
  }, [isVerified, agreed]);

  return webcamOn ? (
    <div
      className={`flex lg:flex-1 w-full flex-col items-end justify-end md:items-center relative md:justify-center`}
    >
      {!isVerified && (
        <>
          <div
            style={{
              height:
                !isMobileOrTab && currentPage !== "VideoConsent"
                  ? "490px"
                  : isTab
                    ? "290px"
                    : isBigDesktop && currentPage === "VideoConsent"
                      ? videoHeight
                      : isBigDesktop
                        ? "600px"
                        : isMobile
                          ? "180px"
                          : videoHeight
                            ? videoHeight
                            : "380px",
              width: isMobile
                ? "100%"
                : isTab
                  ? "70%"
                  : currentPage === "VideoConsent"
                    ? isBigDesktop
                      ? "50%"
                      : isLGDesktop
                        ? "100%"
                        : "70%"
                    : "100%",
            }}
            className={`absolute rounded-xl bg-[#16161680] z-30 `}
          >
            {" "}
          </div>

          <div className="absolute z-40 flex flex-col items-center justify-center h-44 p-0">
            <p className="text-white text-xl text-center mt-0 z-50">
              {`${t("Please hold on while we verify your video")}...`}
            </p>
          </div>
        </>
      )}

      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={true}
        width={
          isMobile
            ? "100%"
            : isTab
              ? "70%"
              : currentPage === "VideoConsent"
                ? isBigDesktop
                  ? "50%"
                  : isLGDesktop
                    ? "100%"
                    : "70%"
                : "100%"
        }
        className={
          isVerified
            ? agreed
              ? "border-2 border-green-600"
              : "border-2 border-red-700"
            : `relative ${currentPage !== "VideoConsent" && "w-hull h-full"} `
        }
        // className="border border-green-150"
        style={{
          objectFit: "contain",
          borderRadius: 16,
          height:
            !isMobileOrTab && currentPage !== "VideoConsent"
              ? "490px"
              : isTab
                ? "290px"
                : isBigDesktop && currentPage === "VideoConsent"
                  ? videoHeight
                  : isBigDesktop
                    ? "600px"
                    : isMobile
                      ? "180px"
                      : videoHeight
                        ? videoHeight
                        : "380px",
          zIndex: 10,
        }}
      />
      <audio ref={micRef} autoPlay playsInline muted={isLocal} />

      {isVerified &&
        agreed &&
        currentPage === "VideoConsent" &&
        !isMobileOrTab && (
          <div className="absolute top-0 right-2 xl:right-[150px] 2xl:right-[265px] z-10 bg-white p-1.5 lg:p-2 mt-2 lg:mt-3 flex items-center rounded-md max-w-max">
            <CheckIcon className="w-4 h-4 lg:w-5 lg:h-5 text-green-250" />
            <p className="ml-2 text-green-250 text-sm lg:text-base mb-0">
              {t("Face detected")}
            </p>
          </div>
        )}

      {isVerified && agreed && isMobileOrTab && (
        <div
          className={`absolute ${isMobile ? "bottom-0.5 right-0 left-0.5" : "top-0 md:right-32"
            }  z-10 bg-white p-1.5 lg:p-2 mt-2 lg:mt-3 flex items-center rounded-b-2xl md:rounded-md w-[98%] md:max-w-max`}
        >
          <CheckIcon className="w-4 h-4 lg:w-5 lg:h-5 text-green-250" />
          <p className="ml-2 text-green-250 text-sm lg:text-base mb-0">
            {t("Face detected")}
          </p>
        </div>
      )}

      {isVerified &&
        agreed &&
        currentPage !== "VideoConsent" &&
        !isMobileOrTab && (
          <div className="bg-[#3BA55D1A] p-1.5 lg:p-2 mt-2 lg:mt-3 flex items-center rounded-md max-w-max">
            <CheckIcon className="w-4 h-4 lg:w-5 lg:h-5 text-green-250" />
            <p className="ml-2 text-green-250 text-sm lg:text-base mb-0">
              {t("Face detected")}
            </p>
          </div>
        )}
    </div>
  ) : null;
};

export default ParticipantView;
